import { useMemo, useState } from "react";
import type { PublicacaoDto } from "@base/contracts";
import { AlertCircle, CheckCircle2, ExternalLink, Eye } from "lucide-react";
import { EstadoVazio, MolduraDoApp } from "@base/ui";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageLayout } from "@/components/layout/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import {
  IconePlataforma,
  ROTULO_PLATAFORMA,
} from "../../../shared/branding/icone-plataforma";
import { LogoRenkeRefs } from "../../../shared/branding/logo-renke-refs";
import { usePropsDoSeletorNaMoldura } from "../../../shared/tenant/projeto.contexto";
import { rodapeDaSessaoNaLateral } from "../../referencias/presentation/rodape-da-sessao";
import { useNavegacaoDoApp } from "../../referencias/presentation/navegacao-do-app";
import { usePublicadosPublicacoes } from "../application/use-publicacoes";
import { ModalPreviewPublicacaoAgenda } from "./modal-preview-publicacao-agenda";

/**
 * Log de publicações que saíram com sucesso (status publicado).
 */
export function PaginaPublicadosPublicacao() {
  const seletor = usePropsDoSeletorNaMoldura();
  const navegacao = useNavegacaoDoApp("publicados-publicacao");
  const { data, isLoading, isError, error } = usePublicadosPublicacoes();
  const [previewId, definirPreviewId] = useState<string | null>(null);

  const itens = useMemo(() => {
    const lista = [...(data?.itens ?? [])];
    return lista.sort((a, b) => {
      const ta = a.publicadoEm ?? a.atualizadoEm;
      const tb = b.publicadoEm ?? b.atualizadoEm;
      return tb.localeCompare(ta);
    });
  }, [data?.itens]);

  return (
    <MolduraDoApp
      nomeDoApp="Creative OS"
      marca={<LogoRenkeRefs />}
      titulo="Publicados"
      subtitulo="Posts que saíram nas redes. Abra o link ou veja o preview."
      navegacao={navegacao}
      rodapeDaLateral={rodapeDaSessaoNaLateral}
      {...seletor}
    >
      <PageLayout className="animate-aparecer space-y-6">
        {isLoading ? (
          <div className="grid gap-3">
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
          </div>
        ) : null}

        {isError ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertTitle>Não carregou a lista</AlertTitle>
            <AlertDescription>
              {(error as Error)?.message ?? "Tente novamente."}
            </AlertDescription>
          </Alert>
        ) : null}

        {!isLoading && itens.length === 0 ? (
          <EstadoVazio
            titulo="Nenhum post publicado ainda"
            descricao="Quando a fila publicar com sucesso, o post aparece aqui com o link da rede."
          />
        ) : null}

        <ul className="grid gap-3">
          {itens.map((p) => (
            <ItemPublicado
              key={p.id}
              publicacao={p}
              onVerPreview={() => definirPreviewId(p.id)}
            />
          ))}
        </ul>
      </PageLayout>

      <ModalPreviewPublicacaoAgenda
        publicacaoId={previewId}
        onFechar={() => definirPreviewId(null)}
      />
    </MolduraDoApp>
  );
}

function ItemPublicado({
  publicacao,
  onVerPreview,
}: {
  readonly publicacao: PublicacaoDto;
  readonly onVerPreview: () => void;
}) {
  const titulo =
    publicacao.nome.trim() || publicacao.legenda.trim() || "(sem nome)";
  const link = publicacao.instagramUrl?.trim() || null;
  const quando = publicacao.publicadoEm
    ? new Date(publicacao.publicadoEm).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <li className="flex flex-col gap-4 rounded-xl border border-success/30 bg-success/5 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <CheckCircle2
            className="size-4 shrink-0 text-success"
            aria-hidden
          />
          <p className="truncate text-sm font-medium text-foreground">
            {titulo}
          </p>
          <Badge
            variant="outline"
            className="inline-flex items-center gap-1.5 font-normal"
          >
            <IconePlataforma
              plataforma={publicacao.plataforma}
              className="size-3.5"
            />
            {ROTULO_PLATAFORMA[publicacao.plataforma]}
          </Badge>
          <Badge variant="successOutline">Publicado</Badge>
        </div>
        {quando ? (
          <p className="text-xs text-muted-foreground">Saiu em {quando}</p>
        ) : null}
        {publicacao.legenda.trim() &&
        publicacao.legenda.trim() !== publicacao.nome.trim() ? (
          <p className="line-clamp-2 text-sm leading-relaxed text-muted-foreground">
            {publicacao.legenda.trim()}
          </p>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-wrap gap-3">
        {link ? (
          <Button type="button" variant="outline" className="cursor-pointer" asChild>
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Abrir postagem de ${titulo}`}
            >
              <ExternalLink className="size-4" aria-hidden />
              Abrir postagem
            </a>
          </Button>
        ) : null}
        <Button
          type="button"
          variant="outline"
          className="cursor-pointer"
          onClick={onVerPreview}
        >
          <Eye className="size-4" aria-hidden />
          Ver preview
        </Button>
      </div>
    </li>
  );
}
